import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Create the context
const NotificationContext = createContext();

// Unique ID generator
const generateId = () => Math.random().toString(36).substring(2, 15);

// Initial state
const initialState = {
  notifications: [],
};

// Action types
const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION';
const CLEAR_NOTIFICATIONS = 'CLEAR_NOTIFICATIONS';

// Reducer function
const notificationReducer = (state, action) => {
  switch (action.type) {
    case ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [
          ...state.notifications,
          { id: generateId(), ...action.payload }
        ]
      };
      
    case REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        )
      };
      
    case CLEAR_NOTIFICATIONS:
      return {
        ...state,
        notifications: []
      };
      
    default:
      return state;
  }
};

// Provider component
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  
  // Add a notification
  const addNotification = useCallback((notification) => {
    const { title, message, type = 'info', autoClose = true, duration = 5000 } = notification;
    
    const notificationPayload = {
      title,
      message,
      type,
      autoClose,
      duration,
      createdAt: new Date()
    };
    
    dispatch({ type: ADD_NOTIFICATION, payload: notificationPayload });
    
    // Auto-close notification if enabled
    if (autoClose) {
      const id = state.notifications[state.notifications.length - 1]?.id;
      
      setTimeout(() => {
        dispatch({ type: REMOVE_NOTIFICATION, payload: id });
      }, duration);
    }
  }, [state.notifications]);
  
  // Remove a notification
  const removeNotification = useCallback((id) => {
    dispatch({ type: REMOVE_NOTIFICATION, payload: id });
  }, []);
  
  // Clear all notifications
  const clearNotifications = useCallback(() => {
    dispatch({ type: CLEAR_NOTIFICATIONS });
  }, []);
  
  // Toast convenience methods
  const toast = {
    info: (title, message, options = {}) => 
      addNotification({ title, message, type: 'info', ...options }),
      
    success: (title, message, options = {}) => 
      addNotification({ title, message, type: 'success', ...options }),
      
    warning: (title, message, options = {}) => 
      addNotification({ title, message, type: 'warning', ...options }),
      
    error: (title, message, options = {}) => 
      addNotification({ title, message, type: 'error', ...options })
  };
  
  return (
    <NotificationContext.Provider
      value={{
        notifications: state.notifications,
        addNotification,
        removeNotification,
        clearNotifications,
        toast
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  
  return context;
};